/**
 * Eligibility rules for the Medical Necessity ("NCCN case gate") feature.
 *
 * Given the live {@link NccnCaseGateConfig} and an order's gate attributes,
 * decides whether the Medical Necessity button should be shown. Each config
 * list is treated as a case-insensitive allow-list; an empty/missing list means
 * that dimension is unrestricted.
 * @module nccnCaseGate
 */
import { NccnCaseGateConfig } from "../../../data-model/nccnCaseGate";

/** Order attributes compared against the case-gate config. */
export interface OrderGateAttributes {
  /** Order `master_auth_status` (e.g. `auth_required`, `auth_by_risa`). */
  authStatus?: string | null;
  /** Raw regimen name (e.g. `NCCN _COL14: (OP Colon) FOLFIRI + ...`); reduced to its code before matching. */
  regimen?: string | null;
}

const normalize = (value: string): string => value.trim().toLowerCase();

/**
 * Extracts the regimen code from a raw regimen name.
 *
 * The regimen name looks like `"NCCN _COL14: (OP Colon) FOLFIRI + ..."`; the
 * code lives between the `NCCN` prefix and the first `:`. Mirrors the backend
 * `extract_regimen` helper so the gate compares codes (e.g. `COL14`) against the
 * config allow-list rather than the full name.
 *
 * @param regimenName - The raw regimen name from the order payload
 * @returns The extracted regimen code, or null when it can't be parsed
 */
export const extractRegimenCode = (
  regimenName: string | null | undefined,
): string | null => {
  if (!regimenName) return null;
  const match = regimenName.match(/^\s*\*?\s*NCCN\s*_\s*([^:]+)/i);
  if (!match) return null;
  const code = match[1]
    .trim()
    .replace(/^[\s_]+/, "")
    .trim();
  return code || null;
};

/**
 * Checks whether a value satisfies an allow-list.
 *
 * @param allowList - Allowed values; empty/missing means "no restriction"
 * @param value - The order's value to test
 * @returns True when unrestricted or when the value is in the allow-list
 */
const matchesAllowList = (
  allowList: string[] | null | undefined,
  value: string | null | undefined,
): boolean => {
  const normalizedAllowList = (allowList ?? [])
    .filter((entry): entry is string => Boolean(entry))
    .map(normalize);
  if (normalizedAllowList.length === 0) return true;
  if (!value) return false;
  return normalizedAllowList.includes(normalize(value));
};

/** Why the Medical Necessity button is gated off (or eligible). */
export type MedicalNecessityGateReason =
  | "config_unavailable"
  | "order_missing"
  | "auth_status_not_allowed"
  | "regimen_not_allowed"
  | null;

/** Result of evaluating the Medical Necessity case gate for one order. */
export interface MedicalNecessityGateResult {
  isEligible: boolean;
  gateReason: MedicalNecessityGateReason;
}

/**
 * Evaluates the Medical Necessity case gate and returns eligibility plus reason.
 *
 * The order must satisfy every populated allow-list in the config
 * (auth status and regimen). The raw regimen name is first reduced to its code
 * via {@link extractRegimenCode} before matching. ICD is not considered for
 * now. A missing config hides the button.
 *
 * @param config - The live case-gate config, or null when unavailable
 * @param order - The order's gate attributes
 * @returns Eligibility flag and a gate reason when blocked
 */
export const evaluateMedicalNecessityGate = (
  config: NccnCaseGateConfig | null,
  order: OrderGateAttributes,
): MedicalNecessityGateResult => {
  if (!config) {
    return { isEligible: false, gateReason: "config_unavailable" };
  }
  if (!matchesAllowList(config.auth_status, order.authStatus)) {
    return { isEligible: false, gateReason: "auth_status_not_allowed" };
  }
  if (!matchesAllowList(config.regimens, extractRegimenCode(order.regimen))) {
    return { isEligible: false, gateReason: "regimen_not_allowed" };
  }
  return { isEligible: true, gateReason: null };
};
